import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { NbComponentStatus, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { NotifierService } from '../shared/notifier.service';
import { SubscribedComponent } from '../subscribed.component';
import {
  IntakeActionName,
  KpiDelta,
  OrganizationIntakeFields,
  OrganizationMetricsRow,
  PlatformOverviewResponse,
  PlatformSummary,
  PlatformOverviewService,
  PlatformTimelineResponse,
} from '../shared/platform-overview.service';
import { activeFilterKeys, DualAxisDelegate, DualAxisEvent } from '../dual-axis/dual-axis.component';
import { ColDef } from '../interfaces/colDef';
import { TreeNode } from '../interfaces/treenode';
import { Paged } from '../interfaces/paged';
import { TableOptions } from '../settings/table-options/table-options.component';
import { columns, otherDefs, stickyColumns } from './coldefs-platform-overview';

interface MonthChoice {
  value: number;
  label: string;
}

interface KpiCard {
  label: string;
  value: string;
  delta: number | null;
  deltaLabel: string | null;
}

// Deterministic per-org initials-avatar colors (hashed by tenant name; stable across sessions).
// First entry follows the brand accent (#1863DC). Muted tones only: no green family
// (DESIGN.md) and every value keeps >=3:1 contrast under white initials.
const AVATAR_PALETTE = ['#1863DC', '#B85AD0', '#B45309', '#155E75', '#0095FF', '#C2417F', '#598BFF', '#7B61FF'];

const INTAKE_FIELDS: (keyof OrganizationIntakeFields)[] = [
  'intake_enabled', 'intake_address_masked', 'intake_month_mails', 'intake_month_direct', 'intake_monthly_cap', 'intake_held',
];

/** Cap input to request body: empty means the default cap (null); a whole number 0 to 65535 is a cap; anything else is invalid (null result). */
export function capPayload(raw: unknown): { monthly_cap: number | null } | null {
  if (raw === null || raw === undefined) { return { monthly_cap: null }; }
  const s = String(raw).trim();
  if (s === '') { return { monthly_cap: null }; }
  if (!/^\d{1,5}$/.test(s)) { return null; }
  const n = Number(s);
  return n <= 65535 ? { monthly_cap: n } : null;
}

@Component({
  selector: 'bst-platform-organizations-table',
  templateUrl: './organizations-table.component.html',
  styleUrls: ['./organizations-table.component.scss'],
  standalone: false
})
export class OrganizationsTableComponent
  extends SubscribedComponent
  implements OnInit, DualAxisDelegate<OrganizationMetricsRow> {

  // === DualAxisDelegate contract ===

  @ViewChild('cellTemplate', { static: true, read: TemplateRef }) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild('cardTemplate', { static: true, read: TemplateRef }) cardTemplateOutlet: TemplateRef<any>;

  data: TreeNode<OrganizationMetricsRow>[] = [];
  stickyColumnsLeft: ColDef[] = stickyColumns;
  standardColumns: ColDef[] = columns;
  stickyColumnsRight: ColDef[] = [];
  otherColumns: ColDef[] = otherDefs;

  options: TableOptions = {
    display: [...stickyColumns, ...columns].map(c => ({ col: c.name, shown: true })),
    others: { status: 'all' },
    filter: {},
    sorts: []
  };

  events: Subject<DualAxisEvent> = new Subject<DualAxisEvent>();
  readonly DualAxisEvent = DualAxisEvent;

  public get filterCount(): number { return activeFilterKeys(this.options?.filter).length; }

  // === KPI strip + table state ===

  count: number | null = null;
  errorMessage: string | null = null;
  kpiCards: KpiCard[] = [];
  responseMonth: string | null = null;
  prevMonthLabel: string = '';

  // === Timeline state ===
  selectedWindow: 6 | 12 | 24 = 12;
  timeline: PlatformTimelineResponse | null = null;
  /** Map of Client_id → points[] for fast sparkline cell lookup. */
  private trendByClientId: Map<number, number[]> = new Map();

  /** All rows from the last fetch, before status filtering. */
  private allRows: OrganizationMetricsRow[] = [];

  // === Intake (spec 5.9) ===
  intakePruneLastOk: string | null = null;
  /** Cap input text per client id; seeded from the row on load. */
  capDrafts: Record<number, string> = {};
  /** Cap text as loaded or last saved; Save only sends a cap the admin changed, so an untouched default is never pinned. */
  capLoaded: Record<number, string> = {};
  private intakeBusy = new Set<number>();

  // === Month selector state ===

  selectedYear: number;
  selectedMonthNum: number;
  years: number[] = [];

  private readonly allMonths: MonthChoice[] = [
    { value: 1,  label: 'January' },
    { value: 2,  label: 'February' },
    { value: 3,  label: 'March' },
    { value: 4,  label: 'April' },
    { value: 5,  label: 'May' },
    { value: 6,  label: 'June' },
    { value: 7,  label: 'July' },
    { value: 8,  label: 'August' },
    { value: 9,  label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  /**
   * Months selectable for `selectedYear`. For the current calendar year, hide
   * months that haven't started yet. The backend rejects future months with
   * 422 `month_in_future`, and the UI shouldn't offer choices that can only
   * produce errors.
   */
  get months(): MonthChoice[] {
    const now = new Date();
    if (this.selectedYear !== now.getFullYear()) return this.allMonths;
    return this.allMonths.slice(0, now.getMonth() + 1);
  }

  constructor(private platform: PlatformOverviewService,
              private notifier: NotifierService,
              private toastr: NbToastrService,
              private translate: TranslateService) {
    super();
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    this.selectedYear = prev.getFullYear();
    this.selectedMonthNum = prev.getMonth() + 1;
    const thisYear = now.getFullYear();
    for (let y = thisYear; y >= thisYear - 4; y--) {
      this.years.push(y);
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    // bst-dual-axis calls paged() on its own first render.
  }

  get selectedMonth(): string {
    return `${this.selectedYear}-${String(this.selectedMonthNum).padStart(2, '0')}`;
  }

  // === Public event handlers ===

  async onMonthChange(): Promise<void> {
    // Year may have just switched from a past year to the current one; if the
    // previously-picked month is now in the future, clamp it back to the
    // newest valid month before refetching.
    const now = new Date();
    if (this.selectedYear === now.getFullYear() && this.selectedMonthNum > now.getMonth() + 1) {
      this.selectedMonthNum = now.getMonth() + 1;
    }
    await this.paged(1);
  }

  async onWindowSelect(months: 6 | 12 | 24): Promise<void> {
    this.selectedWindow = months;
    await this.loadTimeline();
  }

  /** Returns the sparkline values for a given tenant row, or [] if not loaded. */
  trendFor(row: OrganizationMetricsRow): number[] {
    return this.trendByClientId.get(row.client_id) ?? [];
  }

  private async loadTimeline(): Promise<void> {
    try {
      // Anchor the chart's trailing window to the currently-selected month
      // so the KPI strip, the table, and the chart all show the same period.
      // paged() re-fires loadTimeline() after every fetch, so onMonthChange()
      // refreshes the chart transitively via paged(1).
      const response = await this.platform.getTimeline('active_profiles', this.selectedWindow, this.selectedMonth);
      this.timeline = response;
      this.trendByClientId = new Map(response.series.map(s => [s.client_id, s.points]));
    } catch (e) {
      // Non-fatal: chart and sparklines disappear, the rest of the page still works.
      console.error('platform timeline load failed', e);
      this.timeline = null;
      this.trendByClientId = new Map();
    }
  }

  /** Tell dual-axis whether the options change requires a refetch. */
  optionsChange(value: TableOptions): boolean {
    const before = this.options.others?.status;
    const after = value.others?.status;
    if (before !== after) {
      // Status filter changed; re-apply in-process.
      setTimeout(() => this.applyStatusFilter(after ?? 'all'), 0);
    }
    return false; // never need a refetch for client-side filters
  }

  async downloadCsv(): Promise<void> {
    this.errorMessage = null; // clear any stale banner so a successful retry restores the view
    try {
      await this.platform.downloadCsv(this.selectedMonth);
    } catch (e) {
      console.error('platform CSV export failed', e);
      this.errorMessage = 'Export failed, please retry.';
    }
  }

  // === DualAxisDelegate.paged: single-page response wrapping all orgs ===

  async paged(page: number = 1): Promise<Paged<OrganizationMetricsRow>> {
    if (page > 1) {
      // No real pagination; only page 1 has data.
      return this.emptyPage(page);
    }

    this.loading.next(true);
    this.errorMessage = null;
    try {
      const response: PlatformOverviewResponse = await this.platform.getOverview(this.selectedMonth);
      this.responseMonth = response.month;
      this.prevMonthLabel = this.computePrevMonthLabel(response.month);
      this.kpiCards = this.buildKpiCards(response.summary);
      this.allRows = response.organizations;
      this.intakePruneLastOk = response.intake_prune_last_ok ?? null;
      this.capLoaded = Object.fromEntries(response.organizations.map(r =>
        [r.client_id, r.intake_monthly_cap === null || r.intake_monthly_cap === undefined ? '' : String(r.intake_monthly_cap)]));
      this.capDrafts = { ...this.capLoaded };
      this.applyStatusFilter(this.options.others?.status ?? 'all');
      // Fire-and-forget; chart/sparklines populate when this resolves.
      void this.loadTimeline();
      return {
        current_page: 1,
        data: this.data.map(n => n.data),
        from: 1,
        last_page: 1,
        per_page: this.data.length,
        to: this.data.length,
        total: this.data.length
      };
    } catch (e: any) {
      const code: string | undefined = e?.error?.message;
      if (code === 'month_in_future') {
        this.errorMessage = `${this.selectedMonthLabel()} hasn't started yet, pick a closed month.`;
      } else if (code === 'invalid_month_format') {
        this.errorMessage = 'Invalid month format.';
      } else {
        console.error('platform overview load failed', e);
        this.errorMessage = 'Failed to load overview, please retry.';
      }
      this.allRows = [];
      this.intakePruneLastOk = null;
      this.data = [];
      this.count = 0;
      this.kpiCards = [];
      this.responseMonth = null;
      this.prevMonthLabel = '';
      return this.emptyPage(page);
    } finally {
      this.loading.next(false);
    }
  }

  // === Helpers ===

  /**
   * Initials for the avatar fallback when no logo exists.
   * Handles camelCase ("TeamerTime" → "TT"), digit-prefixed names
   * ("42signals" → "SI"), and dot/hyphen-separated brands ("F.L.I.X.-IT" → "FL").
   */
  initialsOf(name: string): string {
    if (!name) return '?';
    const parts = name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(/[\s\-_.]+/)
      .filter(p => /[A-Za-z]/.test(p));
    if (parts.length === 0) return '?';
    if (parts.length === 1) {
      const letters = parts[0].replace(/[^A-Za-z]/g, '');
      return letters.substring(0, 2).toUpperCase() || '?';
    }
    return ((parts[0][0] ?? '') + (parts[1][0] ?? '')).toUpperCase();
  }

  /** Stable color hash for the initials avatar background. */
  colorOf(name: string): string {
    if (!name) return '#8f9bb3';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
  }

  formatBytes(bytes: number): string {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let n = bytes;
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i++;
    }
    return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  formatDate(iso?: string): string {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }

  formatNumber(n: number): string {
    if (n == null) return '-';
    return n.toLocaleString('en-GB');
  }

  get pruneLine(): string {
    if (!this.intakePruneLastOk) {
      return this.translate.instant('platform.overview.intake.prune_never');
    }
    const d = new Date(this.intakePruneLastOk);
    const at = isNaN(d.getTime())
      ? '-'
      : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    return this.translate.instant('platform.overview.intake.prune_ok', { at });
  }

  isIntakeBusy(row: OrganizationMetricsRow): boolean {
    return this.intakeBusy.has(row.client_id);
  }

  /** nb-toggle flips itself; the row value follows it and goes back on a declined confirm or a failed call. */
  async toggleIntake(row: OrganizationMetricsRow, enabled: boolean): Promise<void> {
    const before = !!row.intake_enabled;
    row.intake_enabled = enabled;
    const ok = await this.intakeWrite(row, 'switch', { enabled },
      enabled ? 'platform.overview.intake.switch_on_title' : 'platform.overview.intake.switch_off_title',
      enabled ? 'platform.overview.intake.switch_on_body' : 'platform.overview.intake.switch_off_body',
      enabled ? 'primary' : 'danger');
    if (!ok) {
      row.intake_enabled = before;
    }
  }

  /** Quotes switch (#1697): same confirm, revert and one-action-per-row lock as the intake switch. */
  async toggleQuotes(row: OrganizationMetricsRow, enabled: boolean): Promise<void> {
    const before = !!row.quotes_enabled;
    row.quotes_enabled = enabled;
    const res = await this.rowWrite(row,
      enabled ? 'platform.overview.quotes.switch_on_title' : 'platform.overview.quotes.switch_off_title',
      enabled ? 'platform.overview.quotes.switch_on_body' : 'platform.overview.quotes.switch_off_body',
      enabled ? 'primary' : 'danger', {}, 'platform.overview.quotes.col_enabled',
      () => this.platform.quotesSwitch(row.client_id, enabled));
    row.quotes_enabled = res ? !!res.quotes_enabled : before;
  }

  capDirty(row: OrganizationMetricsRow): boolean {
    return (this.capDrafts[row.client_id] ?? '') !== (this.capLoaded[row.client_id] ?? '');
  }

  /** Enabled with no address (after a revoke): rotate mints a new one. */
  canRotate(row: OrganizationMetricsRow): boolean {
    return !!row.intake_address_masked || !!row.intake_enabled;
  }

  canRevoke(row: OrganizationMetricsRow): boolean {
    return !!row.intake_address_masked;
  }

  addressText(row: OrganizationMetricsRow): string {
    if (row.intake_address_masked) { return row.intake_address_masked; }
    return this.translate.instant(row.intake_enabled ? 'platform.overview.intake.address_revoked' : 'platform.overview.intake.first_switch_on');
  }

  async saveCap(row: OrganizationMetricsRow): Promise<void> {
    if (!this.capDirty(row)) { return; }
    const body = capPayload(this.capDrafts[row.client_id]);
    if (!body) {
      this.toastr.danger(this.translate.instant('platform.overview.intake.cap_invalid'), this.translate.instant('platform.overview.intake.col_cap'));
      return;
    }
    const saved = await this.intakeWrite(row, 'cap', body, 'platform.overview.intake.cap_title', 'platform.overview.intake.cap_body', 'primary',
      { cap: body.monthly_cap === null ? this.translate.instant('platform.overview.intake.default_cap') : body.monthly_cap });
    if (saved) {
      this.toastr.success(this.translate.instant('platform.overview.intake.cap_saved'), this.translate.instant('platform.overview.intake.col_cap'));
    }
  }

  rotateIntake(row: OrganizationMetricsRow): Promise<boolean> {
    return this.intakeWrite(row, 'rotate', {}, 'platform.overview.intake.rotate_title', 'platform.overview.intake.rotate_body', 'danger');
  }

  revokeIntake(row: OrganizationMetricsRow): Promise<boolean> {
    return this.intakeWrite(row, 'revoke', {}, 'platform.overview.intake.revoke_title', 'platform.overview.intake.revoke_body', 'danger');
  }

  /** Confirm, post, apply the returned intake fields. Resolves true on success. */
  private async intakeWrite(row: OrganizationMetricsRow, action: IntakeActionName, body: object,
                            titleKey: string, bodyKey: string, status: NbComponentStatus, params: object = {}): Promise<boolean> {
    const res = await this.rowWrite(row, titleKey, bodyKey, status, params, 'platform.overview.intake.action_' + action,
      () => this.platform.intakeAction(row.client_id, action, body));
    if (!res) { return false; }
    for (const f of INTAKE_FIELDS) {
      if (f in res) { (row as any)[f] = (res as any)[f]; }
    }
    if ('intake_monthly_cap' in res) {
      this.capLoaded[row.client_id] = res.intake_monthly_cap === null || res.intake_monthly_cap === undefined ? '' : String(res.intake_monthly_cap);
      this.capDrafts[row.client_id] = this.capLoaded[row.client_id];
    }
    return true;
  }

  /** Confirm, then post. One action per row at a time. Resolves null when busy, declined or failed (toasted). */
  private async rowWrite<T>(row: OrganizationMetricsRow, titleKey: string, bodyKey: string, status: NbComponentStatus,
                            params: object, toastTitleKey: string, call: () => Promise<T>): Promise<T | null> {
    if (this.intakeBusy.has(row.client_id)) { return null; }
    this.intakeBusy.add(row.client_id);
    try {
      const ok = await this.notifier.confirm(
        this.translate.instant(titleKey, { name: row.client_name }),
        this.translate.instant(bodyKey, { name: row.client_name, ...params }),
        this.translate.instant('platform.overview.intake.confirm_yes'),
        status,
        'no',
      );
      if (!ok) { return null; }
      return await call().catch(e => {
        const message = this.translate.instant(`platform.overview.intake.${e?.status === 419 ? 'session_expired' : 'action_failed'}`);
        this.toastr.danger(message, this.translate.instant(toastTitleKey));
        return null;
      });
    } finally {
      this.intakeBusy.delete(row.client_id);
    }
  }

  // === Internals ===

  private applyStatusFilter(status: string): void {
    const filtered = status === 'all'
      ? this.allRows
      : this.allRows.filter(r => r.status === status);
    this.data = filtered.map(r => ({ data: r, expanded: false, children: [] } as TreeNode<OrganizationMetricsRow>));
    this.count = this.data.length;
  }

  private buildKpiCards(summary: PlatformSummary): KpiCard[] {
    return [
      this.kpiOf('Active profiles', summary.active_profile_count, n => this.formatNumber(n)),
      this.kpiOf('Storage',         summary.storage_bytes,        n => this.formatBytes(n)),
      this.kpiOf('Matching events', summary.matching_events,      n => this.formatNumber(n)),
      this.kpiOf('Organizations',   summary.organizations,        n => this.formatNumber(n))
    ];
  }

  private kpiOf(label: string, kpi: KpiDelta, fmt: (n: number) => string): KpiCard {
    let deltaLabel: string;
    if (kpi.delta > 0) {
      deltaLabel = `↑ +${fmt(kpi.delta)}`;
    } else if (kpi.delta < 0) {
      deltaLabel = `↓ ${fmt(Math.abs(kpi.delta))}`;
    } else {
      deltaLabel = 'no change';
    }
    return {
      label,
      value: fmt(kpi.value),
      delta: kpi.delta,
      deltaLabel
    };
  }

  /** "May 2026" for the currently-selected year/month. Used in error copy. */
  private selectedMonthLabel(): string {
    return new Date(this.selectedYear, this.selectedMonthNum - 1, 1)
      .toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }

  private computePrevMonthLabel(ym: string): string {
    const [yStr, mStr] = ym.split('-');
    const date = new Date(Number(yStr), Number(mStr) - 1, 1);
    date.setMonth(date.getMonth() - 1);
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }

  private emptyPage(page: number): Paged<OrganizationMetricsRow> {
    return {
      current_page: page,
      data: [],
      from: 0,
      last_page: 1,
      per_page: 0,
      to: 0,
      total: 0
    };
  }
}
